import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useI18n } from './i18n';

/**
 * SELECCIÓN MÚLTIPLE ESTILO MONDAY — el mecanismo que usan todas las vistas del CRM.
 *
 * Checkbox por fila, checkbox de "todos" en la cabecera y una barra flotante abajo con el conteo
 * y las acciones. Cada pantalla decide QUÉ acciones ofrece; esto solo maneja la selección y el
 * chrome, para que se comporte igual en todos lados y no haya que reescribirlo por pantalla.
 *
 * Uso:
 *   const sel = useBulkSelect(items.map((i) => i.id));
 *   <BulkCheckbox checked={sel.isSelected(id)} onChange={(e) => sel.toggle(id, e)} />
 *   <BulkBar {...sel} actions={[{ key:'assign', label: t('bulk.assign'), render: … }]} />
 */
export function useBulkSelect(ids: string[]) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);

  // Solo cuentan los ids que siguen en pantalla (si la lista se recarga, lo que ya no está se cae).
  const visible = useMemo(() => new Set(ids), [ids]);
  const picked = useMemo(() => [...selected].filter((id) => visible.has(id)), [selected, visible]);

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  /** Toggle de una fila. Con shift, selecciona el RANGO desde la última tocada (como Monday). */
  const toggle = useCallback((id: string, e?: { shiftKey?: boolean }) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (e?.shiftKey && anchor && anchor !== id) {
        const a = ids.indexOf(anchor);
        const b = ids.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [from, to] = a < b ? [a, b] : [b, a];
          const turnOn = !next.has(id);
          for (let i = from; i <= to; i++) {
            if (turnOn) next.add(ids[i]!); else next.delete(ids[i]!);
          }
          return next;
        }
      }
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setAnchor(id);
  }, [anchor, ids]);

  const allChecked = picked.length > 0 && picked.length === ids.length;
  const someChecked = picked.length > 0 && !allChecked;
  const toggleAll = useCallback(() => {
    setSelected((prev) => (picked.length === ids.length ? new Set<string>() : new Set([...prev, ...ids])));
  }, [ids, picked.length]);
  const clear = useCallback(() => setSelected(new Set()), []);

  const setSelection = useCallback((next: string[]) => setSelected(new Set(next)), []);

  return { selected: picked, count: picked.length, isSelected, toggle, allChecked, someChecked, toggleAll, clear, setSelection };
}

export type BulkSelect = ReturnType<typeof useBulkSelect>;

/**
 * La selección viaja por CONTEXTO, no por props. Las filas están 2-4 niveles debajo de la
 * pantalla (FollowUpRow, las tarjetas del board, las filas de las colas): enhebrar props
 * obligaría a tocar firmas en cadena en cada vista. Con contexto, la fila solo pone
 * <BulkRowCheck id={…} /> y listo; y en una pantalla que todavía no tenga provider, ese
 * checkbox no se pinta y la vista queda exactamente como está hoy.
 */
const BulkContext = createContext<BulkSelect | null>(null);

export function BulkProvider({ sel, children }: { sel: BulkSelect; children: React.ReactNode }) {
  return <BulkContext.Provider value={sel}>{children}</BulkContext.Provider>;
}

export function useBulkCtx(): BulkSelect | null {
  return useContext(BulkContext);
}

/** Checkbox de UNA fila. Sin provider devuelve null (la vista no cambia). */
export function BulkRowCheck({ id, label }: { id: string; label?: string }) {
  const sel = useBulkCtx();
  if (!sel) return null;
  return (
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', flex: 'none' }}>
      <BulkCheckbox checked={sel.isSelected(id)} onChange={(e) => sel.toggle(id, e)} title={label} />
    </span>
  );
}

/** Checkbox de "todos", para la cabecera de la lista. */
export function BulkAllCheck({ label }: { label?: string }) {
  const sel = useBulkCtx();
  if (!sel) return null;
  return (
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <BulkCheckbox checked={sel.allChecked} indeterminate={sel.someChecked} onChange={sel.toggleAll} title={label} />
    </span>
  );
}

/** Checkbox de fila / cabecera. `indeterminate` pinta el guion cuando hay selección parcial. */
export function BulkCheckbox({ checked, indeterminate, onChange, title }: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (e: React.MouseEvent) => void;
  title?: string;
}) {
  return (
    <input
      type="checkbox"
      className="bulk-check"
      title={title}
      checked={checked}
      // El estado "parcial" no existe en el DOM como atributo: se pone por ref.
      ref={(el) => { if (el) el.indeterminate = !!indeterminate && !checked; }}
      onChange={() => { /* el trabajo lo hace onClick, que sí trae shiftKey */ }}
      onClick={(e) => { e.stopPropagation(); onChange(e); }}
    />
  );
}

export type BulkAction = {
  key: string;
  label: string;
  icon?: string;
  /** Acciones destructivas (archivar) van en rojo. */
  danger?: boolean;
  /** Botón simple. */
  onRun?: () => void;
  /** O un control propio (un <select> para elegir equipo/status). */
  render?: () => React.ReactNode;
};

/**
 * Barra flotante de la selección. Aparece abajo cuando hay algo seleccionado, con el conteo y las
 * acciones de esa pantalla. Se cierra con la ✕ (o cuando la acción termina y la lista se recarga).
 */
export function BulkBar({ count, clear, actions, busy }: {
  count: number;
  clear: () => void;
  actions: BulkAction[];
  /** Texto de progreso mientras corre una acción en lote ("3 / 12…"). */
  busy?: string | null;
}) {
  const { t } = useI18n();
  if (!count) return null;
  return (
    <div className="bulk-bar" role="region" aria-label={t('bulk.selected', { n: String(count) })}>
      <span className="bulk-count">{count}</span>
      <strong style={{ fontSize: 'var(--fs-sm)' }}>{t('bulk.selected', { n: String(count) })}</strong>
      <span className="bulk-sep" />
      {busy ? (
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{busy}</span>
      ) : (
        actions.map((a) => (
          <span key={a.key}>
            {a.render ? a.render() : (
              <button
                className="btn ghost sm"
                style={a.danger ? { color: 'var(--red)' } : undefined}
                onClick={a.onRun}
              >
                {a.icon ? `${a.icon} ` : ''}{a.label}
              </button>
            )}
          </span>
        ))
      )}
      <span className="bulk-sep" />
      <button className="btn ghost sm" onClick={clear} title={t('bulk.clear')} aria-label={t('bulk.clear')}><X size={14} strokeWidth={1.75} /></button>
    </div>
  );
}

/**
 * Corre una acción sobre cada claim seleccionado, de a pocos para no ahogar al backend, y
 * devuelve cuántos fallaron: en lote lo normal es que alguno falle (permisos, estado que cambió)
 * y hay que decirlo, no fingir que salió todo bien.
 */
export async function runBulk<T>(
  ids: T[],
  fn: (id: T) => Promise<unknown>,
  onProgress?: (done: number, total: number) => void,
): Promise<{ ok: number; failed: T[] }> {
  const failed: T[] = [];
  let done = 0;
  const CHUNK = 4;
  for (let i = 0; i < ids.length; i += CHUNK) {
    await Promise.all(ids.slice(i, i + CHUNK).map(async (id) => {
      try { await fn(id); } catch { failed.push(id); }
      done += 1;
      onProgress?.(done, ids.length);
    }));
  }
  return { ok: ids.length - failed.length, failed };
}
